import * as yup from 'yup';
import { removeSpecialChars } from '@/compliance/utils/utils';
import {
  isValidLicensePlate,
  isValidVIN,
} from '@/components/VehicleSearchForm/utils';
import { unformatPhone } from '@/utils/index';

type SchemaComplianceFormProps = {
  userName: string;
};

const PHONE_VALIDATION_REGEX =
  /^(?:\+?1?)[2-9]\d{2}[2-9](?!(1)\1)[\d]{2}\d{4}$/;
const IP_V4_REGEX =
  /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
const IP_V6_FULL_REGEX = /^(?:[a-fA-F0-9]{1,4}:){7}[a-fA-F0-9]{1,4}$/;
const IP_V6_MIXED_REGEX =
  /^(?:[a-fA-F0-9]{1,4}:){1,4}:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

export const schemaComplianceForm = ({ userName }: SchemaComplianceFormProps) =>
  yup.object().shape({
    signature: yup
      .string()
      .required('Please type your name, it will be used as a digital signature')
      .test(
        'signature-name-validation',
        'Your signature has to be your full name',
        (value) => {
          return removeSpecialChars(value) == removeSpecialChars(userName);
        }
      ),
    useCase: yup
      .string()
      .max(200)
      .required('Please type your use case, with a maximum of 200 characters'),
    criminalRecordsAgreement: yup.string().required(),
  });

export const schemaListForm = yup
  .object({
    listName: yup
      .string()
      .required('List name is required')
      .matches(
        new RegExp('^[0-9a-zA-Z\\s.,&]+$'),
        'Special characters are not allowed'
      ),
  })
  .required();

export const schemaMileageForm = yup
  .object({
    mileage: yup.number().required('Mileage is required'),
  })
  .required();

export const schemaAddPhoneNumberForm = yup.object({
  phone: yup
    .string()
    .required('Please enter a valid phone number')
    .test(
      'phone-search-validation',
      'Please enter a valid phone number',
      (value) => {
        return (
          unformatPhone(value || '').match(PHONE_VALIDATION_REGEX) !== null
        );
      }
    ),
  password: yup.string().required('Password is a required field'),
});

export const schemaEditNameForm = yup
  .object({
    newFirstName: yup.string().required('This field is required'),
    newLastName: yup.string().required('This field is required'),
    password: yup.string(),
  })
  .required();

export const schemaUpdateEmailForm = yup
  .object({
    newEmail: yup.string().email('Please enter a valid email address'),
    password: yup.string(),
  })
  .required();

export const schemaChangePasswordForm = yup
  .object({
    newPassword: yup.string().min(8, 'Please enter at least 8 characters'),
    newPasswordConfirmation: yup
      .string()
      .oneOf(
        [yup.ref('newPassword')],
        'Your confirmation must match your password above'
      ),
    logoutOtherSessions: yup.boolean(),
    password: yup.string(),
  })
  .required();

export const schemaResetPasswordForm = yup
  .object({
    password: yup
      .string()
      .required('This field is required')
      .min(8, 'Please enter at least 8 characters'),
    passwordConfirmation: yup
      .string()
      .required('This field is required')
      .oneOf(
        [yup.ref('password')],
        'Your confirmation must match your password above'
      ),
    invalidateSessions: yup.boolean(),
  })
  .required();

export const schemaMagicLinkForm = yup.object({
  email: yup
    .string()
    .required('This field is required')
    .email('Please enter a valid email address'),
});

export const schemaResetPasswordRequestForm = schemaMagicLinkForm;
export const schemaReferralForm = schemaMagicLinkForm;

export const schemaLoginForm = yup.object({
  email: yup
    .string()
    .trim()
    .required('This field is required')
    .email('Please enter a valid email address'),
  password: yup.string().required('This field is required'),
});

export const schemaVinSearchForm = yup
  .object({
    vin: yup
      .string()
      .required("We'll need a valid VIN to do a search")
      .max(17)
      .test(
        'vin-validation',
        "We'll need a valid VIN to do a search",
        (value) => {
          return isValidVIN(value);
        }
      ),
  })
  .required();

export const schemaPlateSearchForm = yup
  .object({
    licensePlate: yup
      .string()
      .required("We'll need a valid License Plate to do a search")
      .max(10)
      .test(
        'license-plate-validation',
        "We'll need a valid License Plate to do a search",
        (value) => {
          return isValidLicensePlate(value);
        }
      ),
    state: yup.string().nullable().max(2).required('Select a state'),
  })
  .required();

export const schemaYmmSearchForm = yup
  .object({
    year: yup.string().required('Select a valid year'),
    make: yup.string().required('Select a valid make'),
    model: yup.string().required('Select a valid model'),
    trim: yup.string().required('Select a valid trim'),
  })
  .required();

export const schemaEmailSearchForm = yup.object({
  email: yup
    .string()
    .required('Please enter a valid email to complete search')
    .email('Please enter a valid email to complete search'),
});

export const schemaPhoneSearchForm = yup.object({
  phone: yup
    .string()
    .required('Please enter a valid phone number to complete search')
    .test(
      'phone-search-validation',
      'Please enter a valid phone number to complete search',
      (value) => {
        return (
          unformatPhone(value || '').match(PHONE_VALIDATION_REGEX) !== null
        );
      }
    ),
});

export const schemaUsernameSearchForm = yup.object({
  username: yup
    .string()
    .required(
      'Please enter a valid username or a social media profile name to complete search'
    ),
});

export const schemaPersonSearchForm = yup.object({
  lastName: yup.string().required("We'll need a last name to do a search"),
});

export const schemaMoreInfoForm = yup.object({
  dob: yup
    .string()
    .nullable()
    .test('dob', 'Invalid Date of Birth', function (value) {
      if (value) {
        const dobDate = new Date(value);

        if (dobDate > new Date()) {
          return false;
        }

        if (this.parent.dod) {
          const dodDate = new Date(this.parent.dod);
          return dobDate < dodDate;
        }
      }

      return true;
    })
    .test('dob', 'Date of birth or date of death are empty', function (value) {
      return !!value || !!this.parent.dod;
    }),
  dod: yup
    .string()
    .nullable()
    .test('dod', 'Invalid Date of Death', (value) => {
      if (value) {
        const date = new Date(value);
        return date < new Date();
      }
      return true;
    }),
});

export const schemaPetForm = yup.object({
  display_name: yup.string().required('Please enter a Pet name'),
  species: yup.string().required('Please select a Pet Type'),
  date_of_birth: yup
    .string()
    .nullable()
    .test('date_of_birth', 'Invalid Date of Birth', function (value) {
      const { date_of_adoption: doa, date_of_death: dod } = this.parent;
      if (value) {
        const date = new Date(value);
        if (doa && dod) {
          const adoptionDate = new Date(doa);
          const deathDate = new Date(dod);
          return date < adoptionDate && date < deathDate;
        }
        if (doa) {
          const adoptionDate = new Date(doa);
          return date < adoptionDate;
        }
        if (dod) {
          const deathDate = new Date(dod);
          return date < deathDate;
        }

        return date < new Date();
      }
      return true;
    }),
  date_of_death: yup
    .string()
    .nullable()
    .test('date_of_death', 'Invalid Date of Death', function (value) {
      const { date_of_birth: dob, date_of_adoption: doa } = this.parent;
      if (value) {
        const deathDate = new Date(value);
        if (dob && doa) {
          const birthDate = new Date(dob);
          const adoptionDate = new Date(doa);
          return deathDate > birthDate && deathDate > adoptionDate;
        }
        if (dob) {
          const birthDate = new Date(dob);
          return deathDate > birthDate;
        }
        if (doa) {
          const adoptionDate = new Date(doa);
          return deathDate > adoptionDate;
        }

        return deathDate < new Date();
      }
      return true;
    }),
  date_of_adoption: yup
    .string()
    .nullable()
    .test('date_of_adoption', 'Invalid Date of Adoption', function (value) {
      const { date_of_birth: dob, date_of_death: dod } = this.parent;
      if (value) {
        const adoptionDate = new Date(value);
        if (dob && dod) {
          const birthDate = new Date(dob);
          const deathDate = new Date(dod);
          return adoptionDate > birthDate && adoptionDate < deathDate;
        }
        if (dob) {
          const deathDate = new Date(dob);
          return adoptionDate > deathDate;
        }
        if (dod) {
          const deathDate = new Date(dod);
          return adoptionDate < deathDate;
        }

        return adoptionDate < new Date();
      }
      return true;
    }),
});

export const schemaContactSearchForm = yup.object({
  name: yup.string().when(['title', 'company', 'city', 'state'], {
    is: (title: string, company: string, city: string, state: string) =>
      title === '' && company === '' && city === '' && state === '',
    then: yup.string().required("We'll need some information to run a search"),
  }),
  city: yup.string(),
  state: yup.string().when('city', {
    is: (value: string) => value !== '',
    then: yup.string().required('State is required when City is provided'),
  }),
});

export const schemaAddressSearchForm = yup.object({
  address: yup.string().required("We'll need an address to do a search"),
});

export const schemaVehicleDetailForm = yup.object({
  currentMileage: yup
    .string()
    .nullable()
    .required("Please enter the vehicle's current mileage")
    .test('integer', 'The field should have positive digits only', (value) => {
      return (
        /^\d+$/.test(value || '') &&
        parseFloat(value || '0').toString() === value
      );
    }),
  yearlyMileage: yup
    .string()
    .nullable()
    .required('Please enter your estimated yearly mileage')
    .test('integer', 'The field should have positive digits only', (value) => {
      return (
        /^\d+$/.test(value || '') &&
        parseFloat(value || '0').toString() === value
      );
    }),
  zipCode: yup
    .string()
    .nullable()
    .required("Please enter the vehicle's zip code")
    .max(5, 'Zip code must be exactly 5 digits long')
    .matches(/^\d{5}$/, 'Zip code must be exactly 5 digits long'),
});

export const schemaServiceItemForm = yup.object({
  serviceItemValue: yup.string().required('This cannot be blank'),
});

export const schemaObituarySearchForm = yup
  .object({
    firstName: yup.string().notRequired(),
    lastName: yup.string().required("We'll need a last name to do a search"),
    birthMonth: yup
      .number()
      .nullable(true)
      .transform((value) => (isNaN(value) ? undefined : value))
      .notRequired(),
    birthYear: yup
      .number()
      .nullable(true)
      .max(
        new Date().getFullYear(),
        `Birth year must be less than or equal to ${new Date().getFullYear()}`
      )
      .min(1850, 'Birth year must be greater than or equal to 1850')
      .transform((value) => (isNaN(value) ? undefined : value))
      .notRequired(),
    deathMonth: yup
      .number()
      .nullable(true)
      .transform((value) => (isNaN(value) ? undefined : value))
      .notRequired(),
    deathYear: yup
      .number()
      .nullable(true)
      .max(
        new Date().getFullYear(),
        `Death year must be less than or equal to ${new Date().getFullYear()}`
      )
      .min(1850, 'Death year must be greater than or equal to 1850')
      .test(
        'is-after-birth',
        'Death year must be after birth year',
        function isAfterBirth(deathYear) {
          if (!this.parent.birthYear || !deathYear) return true;

          return deathYear >= this.parent.birthYear;
        }
      )
      .transform((value) => (isNaN(value) ? undefined : value))
      .notRequired(),
    funeralHomeCity: yup.string().notRequired(),
    funeralHomeState: yup.string().notRequired(),
  })
  .required();

const anyCheckBoxPresent = (
  value: boolean | undefined | null,
  context: yup.TestContext
) => {
  return [
    context.parent.not_full_time_residence,
    context.parent.pre_existing_damage,
    context.parent.not_important,
    context.parent.unit_occupied,
    context.parent.dwelling_vacant,
    context.parent.none_apply,
  ].some((item) => item === true);
};

export const schemaBubbleForm = yup.object().shape({
  street_address: yup.string().required(),
  city: yup.string().required(),
  state: yup.string().required(),
  year_built: yup
    .string()
    .test('valid_year', (value) => {
      if (!value) {
        return false;
      }
      return Number(value) < new Date().getFullYear() + 1 && value.length <= 4;
    })
    .required(),
  roof_updated_year: yup
    .string()
    .test('valid_year', (value) => {
      if (!value) {
        return false;
      }
      return Number(value) < new Date().getFullYear() + 1 && value.length <= 4;
    })
    .required(),
  not_full_time_residence: yup
    .boolean()
    .oneOf([true, false])
    .test('house_condition', anyCheckBoxPresent),
  pre_existing_damage: yup
    .boolean()
    .oneOf([true, false])
    .test('house_condition', anyCheckBoxPresent),
  not_important: yup
    .boolean()
    .oneOf([true, false])
    .test('house_condition', anyCheckBoxPresent),
  unit_occupied: yup
    .boolean()
    .oneOf([true, false])
    .test('house_condition', anyCheckBoxPresent),
  dwelling_vacant: yup
    .boolean()
    .oneOf([true, false])
    .test('house_condition', anyCheckBoxPresent),
  none_apply: yup
    .boolean()
    .oneOf([true, false])
    .test('house_condition', anyCheckBoxPresent),
  first_name: yup.string().required(),
  last_name: yup.string().required(),
  phone: yup
    .string()
    .required()
    .test('phone-search-validation', (value) => {
      return unformatPhone(value || '').match(PHONE_VALIDATION_REGEX) !== null;
    }),
});

export const schemaCleverForm = yup.object().shape({
  street_address: yup.string().required(),
  unit: yup.string(),
  city: yup.string().required(),
  state: yup.string().required(),
  zipcode: yup.string().length(5).required(),
  timeframe: yup.string().required(),
  homeBuyingIntention: yup.boolean().oneOf([true, false]).required(),
  phone: yup
    .string()
    .required()
    .test('phone-search-validation', (value) => {
      return unformatPhone(value || '').match(PHONE_VALIDATION_REGEX) !== null;
    }),
});

export const schemaMortyForm = yup.object().shape({
  fico_range: yup.string().required(),
  property_state: yup.string().required(),
  property_zip: yup.string().required(),
  property_city: yup.string().required(),
  first_name: yup.string().required(),
  last_name: yup.string().required(),
  phone: yup
    .string()
    .required()
    .test('phone-search-validation', (value) => {
      return unformatPhone(value || '').match(PHONE_VALIDATION_REGEX) !== null;
    }),
  down_payment_dollars: yup.string().required(),
  purchase_price: yup.string().required(),
  monthly_liabilities: yup.string().required(),
  monthly_income: yup.string().required(),
});

export const schemaListingDetailsLeadsForm = yup.object().shape({
  name: yup
    .string()
    .required('Please enter a full name')
    .matches(
      /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
      'Name can only contain letters.'
    )
    .test('full-name-validation', 'Please enter a full name', (value) => {
      return removeSpecialChars(value).split(' ').length > 1;
    }),
  email: yup.string().email().required('Please enter a valid email'),
  phone: yup
    .string()
    .required('Please enter a phone number')
    .test(
      'phone-search-validation',
      'Please enter a valid phone number',
      (value) => {
        return (
          unformatPhone(value || '').match(PHONE_VALIDATION_REGEX) !== null
        );
      }
    ),
});

export const schemaSmartSearchV1Form = {
  address: yup.object({
    address: yup
      .string()
      .required('Please enter an address or other location complete search'),
  }),
  person: yup.object({
    person: yup.string().required("We'll need a last name to do a search"),
  }),
  email: schemaEmailSearchForm,
  phone: schemaPhoneSearchForm,
  username: schemaUsernameSearchForm,
  vehicle: yup.object({
    vehicle: yup
      .string()
      .required(
        'Please enter a valid VIN number, license plate + state, or year/make/model to complete search'
      ),
  }),
  empty: yup.object({
    empty: yup.string().required("We'll need some information to run a search"),
  }),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as { [key: string]: yup.ObjectSchema<any> };

export const schemaIPSearchForm = yup.object().shape({
  ip: yup
    .string()
    .test('is-ip', 'Please enter a valid IPv4 or IPv6 address', (value) =>
      value
        ? IP_V4_REGEX.test(value) ||
          IP_V6_FULL_REGEX.test(value) ||
          IP_V6_MIXED_REGEX.test(value)
        : false
    ),
});

export const schemaURLSearchForm = yup.object().shape({
  url: yup.string().url('Please enter a valid URL'),
});
